/**
 * WorkspaceGuard - Route guard for workspace-required pages
 *
 * Shows workspace selector when:
 * - User navigates to a workspace-required page
 * - No workspace is currently selected
 * - User has workspaces available (or prompts to create one)
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import WorkspaceModal from './WorkspaceModal';
import { auth } from '../api/client';
import { Building2, FolderPlus, ArrowRight } from 'lucide-react';
import { useI18n } from '../i18n';

// Routes that REQUIRE a workspace to be selected
const WORKSPACE_REQUIRED_ROUTES = [
  '/scans',
  '/vulnerabilities',
  '/credentials',
  '/sessions',
  '/ad',
  '/reports',
  '/attack',
  '/equipment',
  '/enrichment',
  '/antivirus',
  '/mitre-navigator',
];

// Routes that DON'T require workspace
const WORKSPACE_EXEMPT_ROUTES = [
  '/',
  '/dashboard',
  '/workspaces',
  '/account',
  '/pricing',
  '/credits',
  '/support',
  '/notifications',
  '/settings',
  '/error',
];

const WorkspaceGuard = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    hasWorkspace,
    workspaces,
    loading,
    setShowSelector,
    fetchWorkspaces
  } = useWorkspace();

  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [showSelectPrompt, setShowSelectPrompt] = useState(false);

  // Check if current route requires workspace
  const requiresWorkspace = () => {
    const path = location.pathname;
    return WORKSPACE_REQUIRED_ROUTES.some(route => path.startsWith(route));
  };

  // Check if route is exempt from workspace requirement
  const isExemptRoute = () => {
    const path = location.pathname;
    return WORKSPACE_EXEMPT_ROUTES.some(route =>
      path === route || path.startsWith(route + '/')
    );
  };

  useEffect(() => {
    // Skip if not authenticated (including expired tokens) or still loading
    // Check both isAuthenticated (token exists) and isTokenValidClientSide (not expired)
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide() || loading) return;

    // Skip if on exempt route
    if (isExemptRoute()) {
      setShowCreatePrompt(false);
      setShowSelectPrompt(false);
      return;
    }

    // Check if workspace is required
    if (requiresWorkspace() && !hasWorkspace) {
      // Fetch workspaces if not already loaded
      if (workspaces.length === 0) {
        fetchWorkspaces().then((ws) => {
          if (ws && ws.length === 0) {
            // No workspaces - show create prompt
            setShowCreatePrompt(true);
            setShowSelectPrompt(false);
          } else if (ws && ws.length > 0) {
            // Has workspaces - show select prompt
            setShowSelectPrompt(true);
            setShowCreatePrompt(false);
          }
        });
      } else if (workspaces.length === 0) {
        setShowCreatePrompt(true);
        setShowSelectPrompt(false);
      } else {
        setShowSelectPrompt(true);
        setShowCreatePrompt(false);
      }
    } else {
      setShowCreatePrompt(false);
      setShowSelectPrompt(false);
    }
  }, [location.pathname, hasWorkspace, loading, workspaces.length]);

  // Handle "Create Workspace" action
  const handleCreateWorkspace = () => {
    setShowCreatePrompt(false);
    navigate('/workspaces?action=create');
  };

  // Handle "Select Workspace" action
  const handleSelectWorkspace = () => {
    setShowSelectPrompt(false);
    setShowSelector(true);
  };

  // Handle going back to dashboard
  const handleGoBack = () => {
    setShowCreatePrompt(false);
    setShowSelectPrompt(false);
    navigate('/dashboard');
  };

  // Create Workspace Prompt Modal
  if (showCreatePrompt) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

        {/* Modal */}
        <div className="relative w-full max-w-md mx-4 bg-brand-dark border border-brand-green/30
                        rounded-xl shadow-2xl animate-in zoom-in-95 fade-in duration-200 p-6">

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-green/20
                            flex items-center justify-center">
              <Building2 className="w-8 h-8 text-brand-green" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('workspace.createRequired', 'Workspace Required')}
            </h2>
            <p className="text-gray-400 text-sm">
              {t('workspace.createRequiredDescription',
                'You need a workspace to access this section. Create your first workspace to continue.')}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleCreateWorkspace}
              className="w-full flex items-center justify-center gap-2 px-6 py-3
                         bg-brand-green text-black font-medium rounded-lg
                         hover:bg-brand-green/90 transition-colors"
            >
              <FolderPlus className="w-5 h-5" />
              {t('workspace.createNew', 'Create Workspace')}
            </button>

            <button
              onClick={handleGoBack}
              className="w-full flex items-center justify-center gap-2 px-6 py-3
                         bg-brand-black/50 text-gray-400 font-medium rounded-lg
                         hover:text-white hover:bg-brand-black/70 transition-colors
                         border border-brand-green/20"
            >
              {t('common.goBack', 'Go Back to Dashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Select Workspace Prompt Modal
  if (showSelectPrompt) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

        {/* Modal */}
        <div className="relative w-full max-w-md mx-4 bg-brand-dark border border-brand-green/30
                        rounded-xl shadow-2xl animate-in zoom-in-95 fade-in duration-200 p-6">

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-green/20
                            flex items-center justify-center">
              <Building2 className="w-8 h-8 text-brand-green" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('workspace.selectRequired', 'Select a Workspace')}
            </h2>
            <p className="text-gray-400 text-sm">
              {t('workspace.selectRequiredDescription',
                'Please select a workspace to access this section. You have')} {workspaces.length} {t('workspace.workspacesAvailable', 'workspace(s) available.')}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleSelectWorkspace}
              className="w-full flex items-center justify-center gap-2 px-6 py-3
                         bg-brand-green text-black font-medium rounded-lg
                         hover:bg-brand-green/90 transition-colors"
            >
              {t('workspace.selectWorkspace', 'Select Workspace')}
              <ArrowRight className="w-5 h-5" />
            </button>

            <button
              onClick={handleGoBack}
              className="w-full flex items-center justify-center gap-2 px-6 py-3
                         bg-brand-black/50 text-gray-400 font-medium rounded-lg
                         hover:text-white hover:bg-brand-black/70 transition-colors
                         border border-brand-green/20"
            >
              {t('common.goBack', 'Go Back to Dashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default WorkspaceGuard;
